import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  TrendingDown as TrendingDownIcon,
  Inventory as InventoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import {
  getConnections,
  getProductIssues,
  getProductSummary,
  getProductsByCategory,
  getProductCategories,
  getProductSalesImpact,
} from '../../api/api';

function ProductAnalysis() {
  const { t } = useTranslation();
  const [connections, setConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [products, setProducts] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [salesSummary, setSalesSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [issueType, setIssueType] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [error, setError] = useState(null);
  const [debugUrl, setDebugUrl] = useState(null);
  const [showSalesImpact, setShowSalesImpact] = useState(true);
  const [expandedProduct, setExpandedProduct] = useState(null);

  useEffect(() => {
    loadConnections();
  }, []);

  const loadConnections = async () => {
    try {
      const response = await getConnections();
      setConnections(response.data.data);
      if (response.data.data.length > 0) {
        setSelectedConnection(response.data.data[0].id);
      }
    } catch (error) {
      console.error('Error loading connections:', error);
    }
  };

  useEffect(() => {
    if (selectedConnection) {
      loadProducts();
      loadSummary();
      loadCategories();
      if (showSalesImpact) {
        loadSalesImpact();
      }
    }
  }, [selectedConnection, issueType, selectedCategory, showSalesImpact]);

  const loadProducts = async () => {
    setLoading(true);
    setError(null);
    setDebugUrl(null);
    try {
      const params = {
        issue_type: issueType,
        active_only: true,
      };
      if (selectedCategory) {
        params.category_id = selectedCategory;
      }
      const response = await getProductIssues(selectedConnection, params);
      setProducts(response.data.data || []);
    } catch (error) {
      console.error('Error loading products:', error);
      const errorData = error.response?.data;
      if (errorData) {
        setError(errorData.error || 'Error al cargar productos');
        if (errorData.debug_url) {
          setDebugUrl(errorData.debug_url);
        }
      } else {
        setError('Error al cargar productos');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSalesImpact = async () => {
    try {
      const params = {
        issue_type: issueType,
        months_back: 12,
      };
      if (selectedCategory) {
        params.category_id = selectedCategory;
      }
      const response = await getProductSalesImpact(selectedConnection, params);
      setSalesData(response.data.data || []);
      setSalesSummary(response.data.summary);
    } catch (error) {
      console.error('Error loading sales impact:', error);
      setSalesData([]);
      setSalesSummary(null);
    }
  };

  const loadSummary = async () => {
    try {
      const response = await getProductSummary(selectedConnection);
      setSummary(response.data.summary);
    } catch (error) {
      console.error('Error loading summary:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await getProductCategories(selectedConnection);
      setCategories(response.data.data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const formatCurrency = (value) => {
    if (typeof value !== 'number') {
      value = parseFloat(value) || 0;
    }
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const getIssueLabel = (issue) => {
    const labels = {
      no_purchase: { text: 'Sin precio de compra', color: 'error', icon: <WarningIcon /> },
      purchase_gt_sale: { text: 'Compra > Venta', color: 'error', icon: <WarningIcon /> },
      low_margin: { text: 'Margen bajo (<10%)', color: 'warning', icon: <TrendingDownIcon /> },
      ok: { text: 'OK', color: 'success', icon: <CheckIcon /> },
    };
    return labels[issue] || labels.ok;
  };

  const getMarginColor = (margin) => {
    if (margin < 0) return '#d32f2f'; // Red
    if (margin < 10) return '#f57c00'; // Orange
    if (margin < 20) return '#fbc02d'; // Yellow
    return '#388e3c'; // Green
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES');
  };

  const getSalesDataForProduct = (productId) => {
    return salesData.find(s => s.id === productId) || null;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, color: 'primary.main', fontWeight: 'bold' }}>
        🔍 Análisis de Productos - Precios Compra/Venta
      </Typography>

      {/* Connection Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Selecciona una conexión
          </Typography>
          <TextField
            fullWidth
            select
            value={selectedConnection || ''}
            onChange={(e) => setSelectedConnection(parseInt(e.target.value))}
            SelectProps={{
              native: true,
            }}
          >
            <option value="">-- Selecciona una conexión --</option>
            {connections.map((conn) => (
              <option key={conn.id} value={conn.id}>
                {conn.name}
              </option>
            ))}
          </TextField>
        </CardContent>
      </Card>

      {selectedConnection && (
        <>
          {/* Summary Cards */}
          {summary && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <InventoryIcon sx={{ mr: 1 }} />
                      <Typography color="inherit" gutterBottom>
                        Total Productos
                      </Typography>
                    </Box>
                    <Typography variant="h4">{summary.total_products}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      Compra y Venta activos
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <WarningIcon sx={{ mr: 1 }} />
                      <Typography color="inherit" gutterBottom>
                        Sin Precio Compra
                      </Typography>
                    </Box>
                    <Typography variant="h4">{summary.no_purchase_price}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      {((summary.no_purchase_price / summary.total_products) * 100).toFixed(1)}% del total
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <WarningIcon sx={{ mr: 1 }} />
                      <Typography color="inherit" gutterBottom>
                        Compra &gt; Venta
                      </Typography>
                    </Box>
                    <Typography variant="h4">{summary.purchase_gt_sale}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      Posibles errores de precio
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckIcon sx={{ mr: 1 }} />
                      <Typography color="inherit" gutterBottom>
                        Margen Promedio
                      </Typography>
                    </Box>
                    <Typography variant="h4">{summary.average_margin.toFixed(1)}%</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      {summary.healthy_products} productos correctos
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Sales Impact Summary */}
          {showSalesImpact && salesSummary && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)', color: 'white' }}>
                  <CardContent>
                    <Typography color="inherit" gutterBottom variant="subtitle2">
                      💸 Impacto Económico Total (12 meses)
                    </Typography>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 'bold',
                      color: salesSummary.total_economic_impact < 0 ? '#ff5252' : '#69f0ae'
                    }}>
                      {formatCurrency(salesSummary.total_economic_impact)}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      {salesSummary.total_units_sold.toFixed(0)} unidades vendidas
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', color: '#333' }}>
                  <CardContent>
                    <Typography gutterBottom variant="subtitle2">
                      📦 Productos con Ventas
                    </Typography>
                    <Typography variant="h4">{salesSummary.products_with_sales}</Typography>
                    <Typography variant="caption">
                      {salesSummary.products_without_sales} sin ventas en el período
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', color: '#333' }}>
                  <CardContent>
                    <Typography gutterBottom variant="subtitle2">
                      📅 Período Analizado
                    </Typography>
                    <Typography variant="h5">{salesSummary.months_analyzed} meses</Typography>
                    <Typography variant="caption">
                      Desde {formatDate(salesSummary.date_from)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Filters */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Tipo de problema</InputLabel>
                    <Select
                      value={issueType}
                      label="Tipo de problema"
                      onChange={(e) => setIssueType(e.target.value)}
                    >
                      <MenuItem value="all">Todos los productos</MenuItem>
                      <MenuItem value="no_purchase">Sin precio de compra</MenuItem>
                      <MenuItem value="purchase_gt_sale">Compra &gt; Venta</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Categoría</InputLabel>
                    <Select
                      value={selectedCategory}
                      label="Categoría"
                      onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                      <MenuItem value="">Todas las categorías</MenuItem>
                      {categories.map((cat) => (
                        <MenuItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth
                    variant={showSalesImpact ? "contained" : "outlined"}
                    onClick={() => setShowSalesImpact(!showSalesImpact)}
                    sx={{ height: 56 }}
                  >
                    {showSalesImpact ? '✓ Mostrar Impacto Ventas' : 'Mostrar Impacto Ventas'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Products Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Lista de Productos ({products.length})
              </Typography>
              
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {error}
                    </Typography>
                    {debugUrl && (
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Para más información, usa el endpoint: <code>{debugUrl}</code>
                      </Typography>
                    )}
                  </Box>
                </Alert>
              )}
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : products.length === 0 && !error ? (
                <Alert severity="info">No hay productos que cumplan los criterios seleccionados</Alert>
              ) : products.length > 0 && (
                <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ width: 48, fontWeight: 'bold' }}>-</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Nombre</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Categoría</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">
                          Precio Compra
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">
                          Precio Venta
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">
                          Margen %
                        </TableCell>
                        {showSalesImpact && (
                          <>
                            <TableCell sx={{ fontWeight: 'bold' }} align="right">
                              Uds. Vendidas
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }} align="right">
                              <Tooltip title="Impacto económico: (Precio factura - Precio compra) × Cantidad. Positivo = ganancia, Negativo = pérdida">
                                <span>Impacto €</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }} align="center">
                              Última Venta
                            </TableCell>
                          </>
                        )}
                        <TableCell sx={{ fontWeight: 'bold' }} align="center">
                          Estado
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {products.map((product) => {
                        const issueInfo = getIssueLabel(product.issue);
                        const salesInfo = showSalesImpact ? getSalesDataForProduct(product.id) : null;
                        const isExpanded = expandedProduct === product.id;
                        return (
                          <>
                            <TableRow key={`row-${product.id}`} hover sx={{ cursor: 'pointer' }}>
                              <TableCell sx={{ width: 48, padding: '8px' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => setExpandedProduct(isExpanded ? null : product.id)}
                                >
                                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              </TableCell>
                              <TableCell sx={{ fontFamily: 'monospace' }}>
                                {product.default_code || '-'}
                              </TableCell>
                              <TableCell>{product.name}</TableCell>
                              <TableCell>
                                <Typography variant="caption">
                                  {Array.isArray(product.category) ? product.category[1] : product.category}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: '600' }}>
                                {formatCurrency(product.standard_price)}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: '600' }}>
                                {formatCurrency(product.list_price)}
                              </TableCell>
                              <TableCell 
                                align="right" 
                                sx={{ 
                                  fontWeight: 'bold',
                                  color: getMarginColor(product.margin)
                                }}
                              >
                                {product.margin.toFixed(2)}%
                              </TableCell>
                              {showSalesImpact && (
                                <>
                                  <TableCell align="right" sx={{ fontWeight: '600' }}>
                                    {salesInfo ? salesInfo.units_sold.toFixed(0) : '-'}
                                  </TableCell>
                                  <TableCell 
                                    align="right" 
                                    sx={{ 
                                      fontWeight: 'bold',
                                      color: salesInfo ? (salesInfo.economic_impact < 0 ? '#d32f2f' : '#388e3c') : 'inherit'
                                    }}
                                  >
                                    {salesInfo ? formatCurrency(salesInfo.economic_impact) : '-'}
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography variant="caption">
                                      {salesInfo ? formatDate(salesInfo.last_sale_date) : '-'}
                                    </Typography>
                                  </TableCell>
                                </>
                              )}
                              <TableCell align="center">
                                <Chip
                                  icon={issueInfo.icon}
                                  label={issueInfo.text}
                                  color={issueInfo.color}
                                  size="small"
                                />
                              </TableCell>
                            </TableRow>
                            <TableRow key={`expand-${product.id}`}>
                              <TableCell colSpan={showSalesImpact ? 11 : 8} sx={{ padding: 0 }}>
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                  <Box sx={{ p: 2, backgroundColor: '#fafafa' }}>
                                    <Grid container spacing={3}>
                                      {/* Información del Producto */}
                                      <Grid item xs={12} sm={6}>
                                        <Box sx={{ pb: 2, borderBottom: '1px solid #eee' }}>
                                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                            📦 Detalles del Producto
                                          </Typography>
                                          <Box sx={{ ml: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                              <Typography variant="caption" sx={{ opacity: 0.7 }}>ID Producto:</Typography>
                                              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{product.id}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                              <Typography variant="caption" sx={{ opacity: 0.7 }}>Tipo:</Typography>
                                              <Typography variant="caption">{product.type || '-'}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                              <Typography variant="caption" sx={{ opacity: 0.7 }}>Activo:</Typography>
                                              <Typography variant="caption">{product.active ? '✓ Sí' : '✗ No'}</Typography>
                                            </Box>
                                          </Box>
                                        </Box>
                                      </Grid>

                                      {/* Información de Precios */}
                                      <Grid item xs={12} sm={6}>
                                        <Box sx={{ pb: 2, borderBottom: '1px solid #eee' }}>
                                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                            💰 Análisis de Precios
                                          </Typography>
                                          <Box sx={{ ml: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                              <Typography variant="caption" sx={{ opacity: 0.7 }}>Coste (Compra):</Typography>
                                              <Typography variant="caption" sx={{ fontWeight: 600, color: '#d32f2f' }}>{formatCurrency(product.standard_price)}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                              <Typography variant="caption" sx={{ opacity: 0.7 }}>Precio Venta:</Typography>
                                              <Typography variant="caption" sx={{ fontWeight: 600, color: '#388e3c' }}>{formatCurrency(product.list_price)}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                              <Typography variant="caption" sx={{ opacity: 0.7 }}>Margen Unitario:</Typography>
                                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                {formatCurrency(product.list_price - product.standard_price)} ({product.margin.toFixed(2)}%)
                                              </Typography>
                                            </Box>
                                          </Box>
                                        </Box>
                                      </Grid>

                                      {/* Información de Ventas */}
                                      {showSalesImpact && salesInfo && (
                                        <>
                                          <Grid item xs={12} sm={6}>
                                            <Box sx={{ pb: 2, borderBottom: '1px solid #eee' }}>
                                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                📊 Datos de Ventas
                                              </Typography>
                                              <Box sx={{ ml: 2 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                                  <Typography variant="caption" sx={{ opacity: 0.7 }}>Unidades Vendidas:</Typography>
                                                  <Typography variant="caption" sx={{ fontWeight: 600 }}>{salesInfo.units_sold.toFixed(0)}</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                                  <Typography variant="caption" sx={{ opacity: 0.7 }}>Impacto Económico:</Typography>
                                                  <Typography 
                                                    variant="caption" 
                                                    sx={{ 
                                                      fontWeight: 600,
                                                      color: salesInfo.economic_impact < 0 ? '#d32f2f' : '#388e3c'
                                                    }}
                                                  >
                                                    {formatCurrency(salesInfo.economic_impact)}
                                                  </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                                  <Typography variant="caption" sx={{ opacity: 0.7 }}>Impacto por Unidad:</Typography>
                                                  <Typography 
                                                    variant="caption" 
                                                    sx={{ 
                                                      fontWeight: 600,
                                                      color: (salesInfo.economic_impact / salesInfo.units_sold) < 0 ? '#d32f2f' : '#388e3c'
                                                    }}
                                                  >
                                                    {formatCurrency(salesInfo.economic_impact / salesInfo.units_sold)}
                                                  </Typography>
                                                </Box>
                                              </Box>
                                            </Box>
                                          </Grid>

                                          <Grid item xs={12} sm={6}>
                                            <Box sx={{ pb: 2, borderBottom: '1px solid #eee' }}>
                                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                📅 Período de Ventas
                                              </Typography>
                                              <Box sx={{ ml: 2 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                                  <Typography variant="caption" sx={{ opacity: 0.7 }}>Primera Venta:</Typography>
                                                  <Typography variant="caption">{salesInfo.first_sale_date ? formatDate(salesInfo.first_sale_date) : '-'}</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                                  <Typography variant="caption" sx={{ opacity: 0.7 }}>Última Venta:</Typography>
                                                  <Typography variant="caption">{formatDate(salesInfo.last_sale_date)}</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                                  <Typography variant="caption" sx={{ opacity: 0.7 }}>Número de Facturas:</Typography>
                                                  <Typography variant="caption" sx={{ fontWeight: 600 }}>{salesInfo.invoice_count || '-'}</Typography>
                                                </Box>
                                              </Box>
                                            </Box>
                                          </Grid>
                                        </>
                                      )}

                                      {showSalesImpact && !salesInfo && (
                                        <Grid item xs={12}>
                                          <Alert severity="info" sx={{ mt: 1 }}>
                                            <Typography variant="caption">
                                              Este producto no tiene registros de ventas en el período seleccionado.
                                            </Typography>
                                          </Alert>
                                        </Grid>
                                      )}
                                    </Grid>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}

export default ProductAnalysis;
